import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getAllTickets } from '../services/TicketService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { StaffPageHeader } from '../components/StaffPageHeader';
import {
  TicketDetailedFill,
  EnvelopeOpenFill,
  XCircleFill,
  ClockHistory,
  InboxFill,
} from 'react-bootstrap-icons';

const PAGE_SIZE = 15;

// ── Module-level stable refs ──────────────────────────────────────────────────

function isOpen(t)   { return t.status !== 'Closed'; }
function isClosed(t) { return t.status === 'Closed'; }
function isRecent(t) {
  return Date.now() - new Date(t.timestamp).getTime() < 7 * 24 * 60 * 60 * 1000;
}

const STATUS_OPTIONS = [
  { key: 'open',   label: 'Open'   },
  { key: 'closed', label: 'Closed' },
  { key: 'all',    label: 'All'    },
];

const STATUS_PREDICATES = {
  open:   isOpen,
  closed: isClosed,
  all:    () => true,
};

const TICKET_SORTERS = {
  date:      t => new Date(t.timestamp).getTime(),
  id:        t => t.id,
  character: t => (t.characterName || '').toLowerCase(),
  subject:   t => (t.subject || '').toLowerCase(),
};

const ticketMatchesQuery = (t, q) =>
  String(t.id).includes(q) ||
  (t.characterName || '').toLowerCase().includes(q) ||
  (t.subject || '').toLowerCase().includes(q);

// ── Hook ──────────────────────────────────────────────────────────────────────

function useTicketTableControls(records) {
  const [query,        setQuery]        = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [sortKey,      setSortKey]      = useState('date');
  const [sortDir,      setSortDir]      = useState('desc');
  const [page,         setPage]         = useState(1);

  const statusFiltered = useMemo(() => {
    const pred = STATUS_PREDICATES[statusFilter] ?? STATUS_PREDICATES.all;
    return records.filter(pred);
  }, [records, statusFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? statusFiltered.filter(t => ticketMatchesQuery(t, q)) : statusFiltered;
  }, [statusFiltered, query]);

  const sorted = useMemo(() => {
    const getter = TICKET_SORTERS[sortKey];
    if (!getter) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getter(a), bv = getter(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setPage(1); }, [query, statusFilter, sortKey, sortDir]);

  function handleSort(key) {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  }

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage  = Math.min(page, pageCount);
  const start     = (safePage - 1) * PAGE_SIZE;

  return {
    query, setQuery,
    statusFilter, setStatusFilter,
    sortKey, sortDir, handleSort,
    page: safePage, setPage,
    pageCount,
    pageItems:     sorted.slice(start, start + PAGE_SIZE),
    totalFiltered: sorted.length,
    total:         records.length,
  };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SortableHeader({ label, colKey, sortKey, sortDir, onSort }) {
  const active = colKey === sortKey;
  return (
    <th
      className={`stp-th stp-th-sortable${active ? ' stp-th-active' : ''}`}
      onClick={() => onSort(colKey)}
    >
      {label}
      <span className="stp-sort-icon">{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
    </th>
  );
}

function Pagination({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  return (
    <div className="stp-pagination">
      <button className="stp-page-btn" onClick={() => onPage(1)}          disabled={page === 1}>«</button>
      <button className="stp-page-btn" onClick={() => onPage(page - 1)}   disabled={page === 1}>‹</button>
      <span className="stp-page-info">Page {page} of {pageCount}</span>
      <button className="stp-page-btn" onClick={() => onPage(page + 1)}   disabled={page === pageCount}>›</button>
      <button className="stp-page-btn" onClick={() => onPage(pageCount)}  disabled={page === pageCount}>»</button>
    </div>
  );
}

function TicketStatusBadge({ status }) {
  const closed = (status || '').toLowerCase() === 'closed';
  return (
    <span className={`stp-badge${closed ? ' stp-badge--closed' : ' stp-badge--open'}`}>
      {status}
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function StaffTicketPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const history = useHistory();

  const {
    query, setQuery,
    statusFilter, setStatusFilter,
    sortKey, sortDir, handleSort,
    page, setPage,
    pageCount,
    pageItems,
    totalFiltered,
    total,
  } = useTicketTableControls(tickets);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getAllTickets();
        if (!cancelled) setTickets(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const openCount   = useMemo(() => tickets.filter(isOpen).length,   [tickets]);
  const closedCount = useMemo(() => tickets.filter(isClosed).length, [tickets]);
  const recentCount = useMemo(() => tickets.filter(isRecent).length, [tickets]);

  if (loading) return <LoadingSpinner />;
  if (error)   return <p className="stp-error">Failed to load tickets: {error}</p>;

  const sortProps = { sortKey, sortDir, onSort: handleSort };

  return (
    <div className="stp-page">
      <StaffPageHeader icon={<TicketDetailedFill />} title="Support Tickets" />

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      <div className="stp-stats">
        <div className="stp-stat">
          <div className="stp-stat-icon"><InboxFill /></div>
          <div className="stp-stat-value">{total}</div>
          <div className="stp-stat-label">Total</div>
        </div>
        <div className="stp-stat stp-stat--open">
          <div className="stp-stat-icon"><EnvelopeOpenFill /></div>
          <div className="stp-stat-value">{openCount}</div>
          <div className="stp-stat-label">Open</div>
        </div>
        <div className="stp-stat stp-stat--closed">
          <div className="stp-stat-icon"><XCircleFill /></div>
          <div className="stp-stat-value">{closedCount}</div>
          <div className="stp-stat-label">Closed</div>
        </div>
        <div className="stp-stat stp-stat--recent">
          <div className="stp-stat-icon"><ClockHistory /></div>
          <div className="stp-stat-value">{recentCount}</div>
          <div className="stp-stat-label">Last 7 Days</div>
        </div>
      </div>

      {/* ── Controls ──────────────────────────────────────────────────────── */}
      <div className="stp-controls">
        <input
          type="text"
          className="stp-search"
          placeholder="Search by character, subject, or ID…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div className="stp-filter-group">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              className={`stp-filter-btn${statusFilter === opt.key ? ' stp-filter-btn--active' : ''}`}
              onClick={() => setStatusFilter(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="stp-result-count">
          {totalFiltered !== total ? `${totalFiltered} of ${total}` : total}
          {' '}ticket{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {tickets.length === 0 ? (
        <EmptyState message="No tickets yet." />
      ) : totalFiltered === 0 ? (
        <EmptyState message="No tickets match your filters." />
      ) : (
        <>
          <div className="stp-table-wrap">
            <table className="stp-table">
              <thead>
                <tr>
                  <SortableHeader label="ID"        colKey="id"        {...sortProps} />
                  <SortableHeader label="Character" colKey="character" {...sortProps} />
                  <SortableHeader label="Subject"   colKey="subject"   {...sortProps} />
                  <th className="stp-th">Status</th>
                  <SortableHeader label="Opened"    colKey="date"      {...sortProps} />
                </tr>
              </thead>
              <tbody>
                {pageItems.map(t => (
                  <tr key={t.id} className="stp-tr" onClick={() => history.push('/ticket/' + t.id)}>
                    <td className="stp-td stp-td-id">#{t.id}</td>
                    <td className="stp-td">{t.characterName}</td>
                    <td className="stp-td stp-td-subject" title={t.subject}>{t.subject}</td>
                    <td className="stp-td"><TicketStatusBadge status={t.status} /></td>
                    <td className="stp-td stp-td-date">{new Date(t.timestamp).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageCount={pageCount} onPage={setPage} />
        </>
      )}
    </div>
  );
}
